import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

const FIELDS = [
  { name: "name", label: "Name (optional)", placeholder: "Jane Doe" },
  { name: "role", label: "Job / Role", placeholder: "Regional Property Manager" },
  { name: "portfolio_type", label: "Portfolio Type", placeholder: "Residential, commercial, mixed…" },
  { name: "units", label: "Approx. Units / Square Footage", placeholder: "e.g. 450 units / 120,000 sq ft" },
  { name: "software_used", label: "Software Currently Used", placeholder: "DoorLoop, AppFolio…" },
  { name: "previous_software", label: "Software Previously Used", placeholder: "Buildium, spreadsheets…" },
];

const TEXTAREAS = [
  { name: "likes", label: "What do you like about it?" },
  { name: "dislikes", label: "What do you dislike about it?" },
];

export default function ShareYourStack() {
  const [form, setForm] = useState({ name: "", role: "", portfolio_type: "", units: "", software_used: "", previous_software: "", likes: "", dislikes: "", permission_to_quote: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/feedback", form);
      setDone(true);
      toast.success("Thank you for sharing your stack.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center">
        <Seo title="Thank you" noindex />
        <p className="font-serif-display text-4xl font-black text-stone-900">Thank you.</p>
        <p className="text-lg text-stone-600 mt-4">Your submission helps us build reviews grounded in real operator experience. It stays private unless you gave permission to quote — and even then, always anonymized.</p>
      </div>
    );
  }

  return (
    <div>
      <Seo title="Share Your Stack" description="Tell us which property management tools you use, love and hate." />
      <section className="border-b-2 border-stone-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
          <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-4">Reader Feedback</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black text-stone-900">Share Your Stack</h1>
          <p className="text-lg text-stone-600 mt-4">The best reviews come from people in the trenches. Tell us what you actually use and how it performs. Submissions are private by default.</p>
        </div>
      </section>

      <form onSubmit={submit} data-testid="share-stack-form" className="max-w-3xl mx-auto px-4 sm:px-8 py-12 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {FIELDS.map((f) => (
            <div key={f.name}>
              <label className="font-mono-pm text-[11px] tracking-widest uppercase text-stone-600 block mb-2">{f.label}</label>
              <input data-testid={`field-${f.name}`} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder}
                className="w-full border border-stone-900 bg-white px-4 py-3 font-sans-pm text-stone-900 focus:outline-none focus:hard-shadow transition-shadow" />
            </div>
          ))}
        </div>

        {TEXTAREAS.map((f) => (
          <div key={f.name}>
            <label className="font-mono-pm text-[11px] tracking-widest uppercase text-stone-600 block mb-2">{f.label}</label>
            <textarea data-testid={`field-${f.name}`} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} rows={4}
              className="w-full border border-stone-900 bg-white px-4 py-3 font-sans-pm text-stone-900 focus:outline-none focus:hard-shadow transition-shadow" />
          </div>
        ))}

        <label className="flex items-start gap-3 border border-stone-900 bg-white p-4 cursor-pointer">
          <input type="checkbox" data-testid="field-permission" checked={form.permission_to_quote}
            onChange={(e) => set("permission_to_quote", e.target.checked)} className="mt-1 w-4 h-4 accent-stone-900" />
          <span className="text-sm text-stone-700">I give permission for The PM Test to <strong>anonymously</strong> quote my feedback. My name and identifying details will never be published.</span>
        </label>

        <button type="submit" disabled={submitting} data-testid="submit-feedback"
          className="bg-stone-900 text-white px-10 py-4 font-mono-pm font-bold text-sm tracking-widest uppercase hover:-translate-y-1 hover:hard-shadow-red transition-transform duration-200 disabled:opacity-50">
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </form>
    </div>
  );
}
